import type { FunnelAnalytics, RetentionAnalytics } from '@coachapp/shared';
import { ArrowRight, Calculator, Info } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import ErrorAlert from '../components/ErrorAlert';
import { ScreenHeader } from '../components/layout/ScreenHeader';
import TransitionLink from '../components/TransitionLink';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import {
  getAnalyticsStatMeta,
  isAnalyticsStatId,
  type AnalyticsStatId,
} from '../lib/analytics-stats';
import { getApiErrorMessage } from '../lib/api-error';
import { computeDashboardAnalytics } from '../lib/dashboard-analytics';
import {
  useGetBookingsQuery,
  useGetClientsQuery,
  useGetFunnelQuery,
  useGetRetentionQuery,
  useGetTrainingPlansQuery,
} from '../store/coachApi';

type StatBreakdownItem = {
  label: string;
  value: string;
};

function formatStatValue(
  statId: AnalyticsStatId,
  context: {
    clientsCount: number;
    plansCount: number;
    analytics: ReturnType<typeof computeDashboardAnalytics>;
    retention: RetentionAnalytics | null;
    funnel: FunnelAnalytics | null;
    locale: string;
  },
): string {
  const { clientsCount, plansCount, analytics, retention, funnel, locale } = context;

  switch (statId) {
    case 'totalClients':
      return String(clientsCount);
    case 'activeClients':
      return String(analytics.activeClients);
    case 'plans':
      return String(plansCount);
    case 'todaySessions':
      return String(analytics.todaySessions);
    case 'weekSessions':
      return String(analytics.weekSessions);
    case 'pending':
      return String(analytics.pendingCount);
    case 'churn':
      return retention ? `${(retention.churnRate30d * 100).toFixed(1)}%` : '—';
    case 'ltv':
      return retention
        ? `${(retention.avgLtvCents / 100).toLocaleString(locale)} ₽`
        : '—';
    case 'conversion':
      return funnel ? `${(funnel.conversionRate * 100).toFixed(1)}%` : '—';
    default:
      return '—';
  }
}

function getStatBreakdown(
  statId: AnalyticsStatId,
  context: {
    clientsCount: number;
    plansCount: number;
    analytics: ReturnType<typeof computeDashboardAnalytics>;
    retention: RetentionAnalytics | null;
    funnel: FunnelAnalytics | null;
    locale: string;
    t: (key: string, options?: Record<string, unknown>) => string;
  },
): StatBreakdownItem[] {
  const { clientsCount, plansCount, analytics, retention, funnel, locale, t } = context;

  switch (statId) {
    case 'totalClients':
      return [
        { label: t('analyticsStat.breakdown.active'), value: String(analytics.activeClients) },
        { label: t('analyticsStat.breakdown.inactive'), value: String(analytics.inactiveClients) },
      ];
    case 'activeClients':
      return [
        { label: t('analyticsStat.breakdown.total'), value: String(clientsCount) },
        { label: t('analyticsStat.breakdown.inactive'), value: String(analytics.inactiveClients) },
      ];
    case 'plans':
      return [{ label: t('analyticsStat.breakdown.total'), value: String(plansCount) }];
    case 'todaySessions':
      return [
        {
          label: t('analyticsStat.breakdown.weekTotal'),
          value: String(analytics.weekSessions),
        },
      ];
    case 'weekSessions':
      return analytics.last7Days.map(({ day, count }) => ({
        label: day.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' }),
        value: String(count),
      }));
    case 'pending':
      return analytics.statusCounts
        .filter(({ status }) => status === 'PENDING')
        .map(({ count }) => ({
          label: t('status.PENDING'),
          value: String(count),
        }));
    case 'churn':
      return retention
        ? [
            {
              label: t('analyticsStat.breakdown.activeNow'),
              value: String(retention.activeClients),
            },
            {
              label: t('analyticsStat.breakdown.inactiveNow'),
              value: String(retention.inactiveClients),
            },
          ]
        : [];
    case 'ltv':
      return [];
    case 'conversion':
      return funnel
        ? [
            { label: t('leadStage.INQUIRY'), value: String(funnel.inquiry) },
            { label: t('leadStage.TRIAL'), value: String(funnel.trial) },
            { label: t('leadStage.CONVERTED'), value: String(funnel.converted) },
            { label: t('leadStage.LOST'), value: String(funnel.lost) },
          ]
        : [];
    default:
      return [];
  }
}

function getRelatedLink(
  statId: AnalyticsStatId,
  t: (key: string) => string,
): { to: string; label: string } | null {
  switch (statId) {
    case 'totalClients':
    case 'activeClients':
      return { to: '/clients', label: t('analyticsStat.related.clients') };
    case 'plans':
      return null;
    case 'todaySessions':
    case 'weekSessions':
    case 'pending':
      return { to: '/', label: t('analyticsStat.related.schedule') };
    case 'churn':
    case 'ltv':
      return { to: '/clients', label: t('analyticsStat.related.clients') };
    case 'conversion':
      return { to: '/clients?filter=leads', label: t('analyticsStat.related.leads') };
    default:
      return null;
  }
}

const AnalyticsStatDetailPage = () => {
  const { statId } = useParams();
  const navigate = useTransitionNavigate();
  const { t, i18n } = useTranslation();

  const clientsQuery = useGetClientsQuery();
  const bookingsQuery = useGetBookingsQuery();
  const plansQuery = useGetTrainingPlansQuery();
  const retentionQuery = useGetRetentionQuery();
  const funnelQuery = useGetFunnelQuery();

  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const bookings = useMemo(() => bookingsQuery.data ?? [], [bookingsQuery.data]);
  const plans = plansQuery.data ?? [];
  const retention = retentionQuery.data ?? null;
  const funnel = funnelQuery.data ?? null;

  const dataQueries = [
    clientsQuery,
    bookingsQuery,
    plansQuery,
    retentionQuery,
    funnelQuery,
  ];
  const loading = dataQueries.some((query) => query.isLoading);

  const fetchError = useMemo(() => {
    const err =
      clientsQuery.error ||
      bookingsQuery.error ||
      plansQuery.error ||
      retentionQuery.error ||
      funnelQuery.error;
    return err ? getApiErrorMessage(err, 'Не удалось загрузить данные') : null;
  }, [
    clientsQuery.error,
    bookingsQuery.error,
    plansQuery.error,
    retentionQuery.error,
    funnelQuery.error,
  ]);

  const refetchAll = () => {
    void clientsQuery.refetch();
    void bookingsQuery.refetch();
    void plansQuery.refetch();
    void retentionQuery.refetch();
    void funnelQuery.refetch();
  };

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const analytics = useMemo(
    () => computeDashboardAnalytics(bookings, clients, locale),
    [bookings, clients, locale],
  );

  if (!isAnalyticsStatId(statId)) {
    return <Navigate to="/analytics" replace />;
  }

  const meta = getAnalyticsStatMeta(statId);
  const Icon = meta.icon;

  const valueContext = {
    clientsCount: clients.length,
    plansCount: plans.length,
    analytics,
    retention,
    funnel,
    locale,
  };

  const value = formatStatValue(statId, valueContext);
  const breakdown = getStatBreakdown(statId, { ...valueContext, t });
  const relatedLink = getRelatedLink(statId, t);

  if (loading) {
    return (
      <div className="page-container flex justify-center py-24">
        <div className="spinner h-10 w-10" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <ScreenHeader
        title={t(meta.titleKey)}
        subtitle={t('analyticsStat.detailSubtitle')}
        onBack={() => navigate('/analytics')}
        backLabel={t('analyticsStat.backToAnalytics')}
      />

      {fetchError && (
        <ErrorAlert message={fetchError} onRetry={refetchAll} onDismiss={() => {}} />
      )}

      <div className="stat-card mb-6">
        <div className="flex justify-between items-start gap-3 mb-4">
          <span className="section-label">
            {t('analyticsStat.currentValue')}
          </span>
          <div
            className={`p-2.5 rounded-xl bg-gradient-to-br ${meta.accent} flex items-center justify-center shrink-0`}
          >
            <Icon size={22} />
          </div>
        </div>
        <div className="text-4xl md:text-5xl font-bold text-slate-900 tabular-nums">{value}</div>
      </div>

      <div className="space-y-4">
        <section className="card">
          <div className="flex items-center gap-2 mb-3">
            <Info size={18} className="text-primary shrink-0" />
            <h2 className="text-lg font-bold text-slate-900">{t('analyticsStat.whatItMeans')}</h2>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            {t(`analyticsStat.${statId}.description`)}
          </p>
        </section>

        <section className="card">
          <div className="flex items-center gap-2 mb-3">
            <Calculator size={18} className="text-primary shrink-0" />
            <h2 className="text-lg font-bold text-slate-900">{t('analyticsStat.howCalculated')}</h2>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
            {t(`analyticsStat.${statId}.formula`)}
          </p>
        </section>

        {breakdown.length > 0 && (
          <section className="card">
            <h2 className="text-lg font-bold text-slate-900 mb-4">{t('analyticsStat.breakdown.title')}</h2>
            <ul className="space-y-3">
              {breakdown.map((item) => (
                <li
                  key={item.label}
                  className="flex items-center justify-between gap-3 text-sm border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                >
                  <span className="text-slate-600">{item.label}</span>
                  <span className="font-bold text-slate-900 tabular-nums">{item.value}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {relatedLink && (
          <TransitionLink
            to={relatedLink.to}
            className="card flex items-center justify-between gap-3 hover:border-primary/20 transition-colors"
          >
            <span className="font-semibold text-slate-900">{relatedLink.label}</span>
            <ArrowRight size={18} className="text-primary shrink-0" />
          </TransitionLink>
        )}
      </div>
    </div>
  );
};

export default AnalyticsStatDetailPage;
